import { useMemo } from 'react'
import { useQuery } from 'urql'
import { INBOX_SMS_PROVIDER_QUERY } from 'app/components/contacts/inbox/graphql'
import { smsProviderForLocation, type SmsProvider } from 'app/lib/smsOptOut'

/**
 * Which provider a location texts through. `unknown` while the query is
 * paused, in flight, or errored, so callers that gate behavior on `callrail`
 * stay quiet until we actually know.
 *
 * `cache-first` on purpose: this runs on every thread the user opens, and the
 * document cache keys on query plus variables, so a whole session of clicking
 * through one location's threads costs a single request. Any mutation
 * returning a Location invalidates it, which is coarser than provider config
 * actually changing, and errs toward refetching rather than going stale.
 *
 * Known redundancy: on the contact drawer path the full Location is already
 * loaded by ContactDrawer through LocationStore, whose MODEL_FIELDS include
 * these same fields. Reusing it would mean threading a location prop through
 * ContactRelatedObjects and ActivityPanel, neither of which needs it, to save
 * one cached request. The main inbox route has no such object to reuse (it
 * holds only a locationId from search params), so the query stays here.
 */
export function useSmsProvider(locationId: string | undefined): SmsProvider {
  const [result] = useQuery({
    query: INBOX_SMS_PROVIDER_QUERY,
    variables: { id: locationId ?? `` },
    pause: !locationId,
    requestPolicy: `cache-first`,
  })

  return useMemo(() => smsProviderForLocation(result.data?.location), [result.data?.location])
}
